/**
 * WP Action Processor — LUKA #2: wykonuje akcje final_action NIEZALEZNE od nodow
 * (delete / keep_outer / monitor). publishQueueService iteruje po nodach, wiec
 * wpisy off_topic / adjacent / in_scope bez noda NIGDY tam nie trafia.
 *
 * Idempotencja: UNIQUE INDEX (wp_post_id, final_action) WHERE result='success' w wp_action_logs.
 * Ponowny call skipuje wpisy juz przetworzone. Override zmienia final_action -> nowy log row.
 */

import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";
import { logPublic, logError, logDebug } from "@/services/contentEngine/contentLogger";

const PREFIX = "[WP-ACTION-PROC]";

type LogCb = (
  type: "info" | "success" | "error" | "stage",
  message: string,
  debug?: Record<string, unknown>,
) => void;

interface WebsiteRow {
  id: string;
  url: string;
  login: string | null;
  password: string | null;
  api_key: string | null;
}

async function setupWpCreds(websiteId: string): Promise<WebsiteRow> {
  const { data, error } = await supabase
    .from("websites")
    .select("id, url, login, password, api_key")
    .eq("id", websiteId)
    .maybeSingle();
  if (error || !data) throw new Error("Website not found");
  const w = data as WebsiteRow;
  const username = w.login;
  const password = w.api_key || w.password;
  if (!username || !password) throw new Error("Brak WP credentials dla domeny");
  wordpressService.setCredentials({
    url: w.url.replace(/\/$/, ""),
    username,
    password,
  });
  return w;
}

export interface WpActionProcessorStats {
  processed: number;
  deleted: number;
  kept_outer: number;
  monitored: number;
  skipped_already_processed: number;
  failed: number;
  errors: Array<{ wp_post_id: string; error: string }>;
  duration_ms: number;
}

interface ActionablePost {
  id: string;
  wp_post_id: number;
  title: string | null;
  wp_url: string | null;
  final_action: "delete" | "keep_outer" | "monitor";
  final_action_reason: string | null;
  already_processed: boolean;
}

async function loadActionablePosts(websiteId: string): Promise<ActionablePost[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from("wp_posts")
    .select("id, wp_post_id, title, wp_url, final_action, final_action_reason")
    .eq("website_id", websiteId)
    .eq("final_action_locked", true)
    .in("final_action", ["delete", "keep_outer", "monitor"]);

  const candidates = (data || []) as Array<{
    id: string;
    wp_post_id: number;
    title: string | null;
    wp_url: string | null;
    final_action: "delete" | "keep_outer" | "monitor";
    final_action_reason: string | null;
  }>;

  if (candidates.length === 0) return [];

  // Sprawdz ktore juz zostaly przetworzone (idempotencja)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: logs } = await (supabase as any)
    .from("wp_action_logs")
    .select("wp_post_id, final_action")
    .eq("website_id", websiteId)
    .eq("result", "success")
    .in(
      "wp_post_id",
      candidates.map((c) => c.id),
    );

  const processedKeys = new Set(
    ((logs || []) as Array<{ wp_post_id: string; final_action: string }>).map(
      (l) => `${l.wp_post_id}|${l.final_action}`,
    ),
  );

  return candidates.map((c) => ({
    ...c,
    already_processed: processedKeys.has(`${c.id}|${c.final_action}`),
  }));
}

async function logAction(
  wpPostId: string,
  websiteId: string,
  finalAction: string,
  result: "success" | "failed" | "skipped",
  opts: {
    userId?: string | null;
    errorMessage?: string;
    debug?: Record<string, unknown>;
  } = {},
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).from("wp_action_logs").insert({
    wp_post_id: wpPostId,
    website_id: websiteId,
    final_action: finalAction,
    result,
    processed_by: opts.userId ?? null,
    error_message: opts.errorMessage ?? null,
    debug: opts.debug ?? null,
  });
  if (error) {
    logError(PREFIX, `Log insert failed (${wpPostId}/${finalAction}): ${error.message}`);
  }
}

export async function processWpActionsForWebsite(
  websiteId: string,
  options: {
    userId?: string | null;
    onLog?: LogCb;
  } = {},
): Promise<WpActionProcessorStats> {
  const t0 = Date.now();

  const posts = await loadActionablePosts(websiteId);
  const pending = posts.filter((p) => !p.already_processed);

  logPublic(
    PREFIX,
    `Start — ${pending.length} do przetworzenia (${posts.length - pending.length} juz przetworzonych)`,
  );
  options.onLog?.(
    "info",
    `▶ Wykonanie akcji: ${pending.length} wpisów (${posts.length - pending.length} już wcześniej wykonane)`,
  );

  const stats: WpActionProcessorStats = {
    processed: 0,
    deleted: 0,
    kept_outer: 0,
    monitored: 0,
    skipped_already_processed: posts.length - pending.length,
    failed: 0,
    errors: [],
    duration_ms: 0,
  };

  if (pending.length === 0) {
    stats.duration_ms = Date.now() - t0;
    options.onLog?.(
      "success",
      `✓ Brak wpisów do przetworzenia — wszystko już wykonane.`,
    );
    return stats;
  }

  // Setup WP creds raz (tylko gdy sa delete'y)
  const needsWpAuth = pending.some((p) => p.final_action === "delete");
  if (needsWpAuth) {
    try {
      await setupWpCreds(websiteId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      options.onLog?.("error", `✗ WP credentials: ${msg}`);
      throw err;
    }
  }

  for (const post of pending) {
    stats.processed++;
    try {
      if (post.final_action === "delete") {
        // 1. Soft delete w WP (status=draft) — sprawdz wynik!
        const wpRes = await wordpressService.softDeletePost(post.wp_post_id);
        if (!wpRes.success) {
          throw new Error(
            `WP softDeletePost failed: ${wpRes.error ?? "unknown error"}`,
          );
        }
        // 2. Zapisz do wp_posts_trash
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any).from("wp_posts_trash").insert({
          wp_post_id: post.id,
          website_id: websiteId,
          wp_remote_post_id: post.wp_post_id,
          title: post.title,
          wp_url: post.wp_url,
          status_before_delete: "publish",
          soft_deleted_by: options.userId ?? null,
          soft_delete_reason: post.final_action_reason ?? null,
        });
        // 3. Zsynchronizuj wp_posts.status='draft' + flag action_executed_at
        //    żeby UI banner ich nie pokazywał jako pending bez pełnego sync.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any)
          .from("wp_posts")
          .update({ status: "draft", action_executed_at: new Date().toISOString() })
          .eq("id", post.id);
        await logAction(post.id, websiteId, "delete", "success", {
          userId: options.userId,
          debug: { wp_remote_post_id: post.wp_post_id, wp_url: post.wp_url },
        });
        stats.deleted++;
        options.onLog?.(
          "success",
          `  ✓ Soft delete: "${post.title?.slice(0, 60) ?? post.id}" (WP status=draft)`,
        );
      } else if (post.final_action === "keep_outer") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any)
          .from("wp_posts")
          .update({ action_executed_at: new Date().toISOString() })
          .eq("id", post.id);
        await logAction(post.id, websiteId, "keep_outer", "success", {
          userId: options.userId,
          debug: { noop_reason: "keep_outer means leave it alone" },
        });
        stats.kept_outer++;
        logDebug(PREFIX, `keep_outer noop: ${post.id}`);
      } else if (post.final_action === "monitor") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any)
          .from("wp_posts")
          .update({ action_executed_at: new Date().toISOString() })
          .eq("id", post.id);
        await logAction(post.id, websiteId, "monitor", "success", {
          userId: options.userId,
          debug: { noop_reason: "user review pending" },
        });
        stats.monitored++;
        logDebug(PREFIX, `monitor noop: ${post.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logError(PREFIX, `Action ${post.final_action} failed for ${post.id}: ${msg}`);
      stats.failed++;
      stats.errors.push({ wp_post_id: post.id, error: msg });
      await logAction(post.id, websiteId, post.final_action, "failed", {
        userId: options.userId,
        errorMessage: msg,
      });
      options.onLog?.("error", `  ✗ ${post.final_action} failed: ${msg}`);
    }
  }

  stats.duration_ms = Date.now() - t0;
  logPublic(
    PREFIX,
    `Done ${stats.duration_ms}ms: deleted=${stats.deleted} kept_outer=${stats.kept_outer} monitored=${stats.monitored} failed=${stats.failed}`,
  );
  options.onLog?.(
    "stage",
    `✓ Akcje wykonane: ${stats.deleted} delete, ${stats.kept_outer} keep_outer, ${stats.monitored} monitor${stats.failed > 0 ? ` (${stats.failed} błędów)` : ""}`,
    { stats },
  );

  return stats;
}

export interface TrashRow {
  id: string;
  wp_post_id: string;
  website_id: string;
  wp_remote_post_id: number;
  title: string | null;
  wp_url: string | null;
  status_before_delete: string | null;
  soft_deleted_at: string;
  soft_deleted_by: string | null;
  soft_delete_reason: string | null;
  restored_at: string | null;
}

export async function getTrashForWebsite(websiteId: string): Promise<TrashRow[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("wp_posts_trash")
    .select("*")
    .eq("website_id", websiteId)
    .is("restored_at", null)
    .order("soft_deleted_at", { ascending: false });
  if (error) throw error;
  return (data || []) as TrashRow[];
}

export async function restoreFromTrash(
  trashId: string,
  userId?: string | null,
): Promise<{ restored: boolean; wp_url: string | null }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: trash } = await (supabase as any)
    .from("wp_posts_trash")
    .select("id, wp_post_id, website_id, wp_remote_post_id, wp_url, status_before_delete")
    .eq("id", trashId)
    .maybeSingle();

  const t = trash as {
    id: string;
    wp_post_id: string;
    website_id: string;
    wp_remote_post_id: number;
    wp_url: string | null;
    status_before_delete: string | null;
  } | null;
  if (!t) throw new Error("Trash entry not found");

  await setupWpCreds(t.website_id);
  await wordpressService.restorePost(t.wp_remote_post_id, t.status_before_delete || "publish");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts_trash")
    .update({
      restored_at: new Date().toISOString(),
      restored_by: userId ?? null,
    })
    .eq("id", trashId);
  if (error) throw error;

  return { restored: true, wp_url: t.wp_url };
}
